import { motion } from 'framer-motion';
import { BookOpenText, FileQuestion, Mail, MoveUpRight, Send } from 'lucide-react';
import { useSetRecoilState } from 'recoil';

import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { INLINE_CONTACT_FORM_TYPES } from '@/lib/constants';
import { dialogState } from '@/state/dialog';
import { tv } from '@/utils/styles';

import { AppNavigationContactForm } from '../contact-form';

interface Props {
  isOpen: boolean;
}

const AppNavigationHelpDropdown = ({ isOpen: navIsOpen }: Props) => {
  const setDialog = useSetRecoilState(dialogState);

  const handleContactSupportClick = () => {
    setDialog({
      isOpen: true,
      maxWidth: '680px',
      title: `Submit a support request`,
      body: (
        <>
          If you are having any issues with the app, your account or if you have a question about our product, please
          submit a support request with as much detail as possible and we&apos;ll get back to you ASAP.
          <AppNavigationContactForm type={INLINE_CONTACT_FORM_TYPES.SUPPORT} />
        </>
      ),
      footnote:
        'We typically respond to support requests within 24 hours, but please allow up to 48 hours for a response in some circumstances.',
    });
  };

  const handleGiveFeedbackClick = () => {
    setDialog({
      isOpen: true,
      maxWidth: '680px',
      title: `Submit feedback`,
      body: (
        <>
          If you have any feedback or suggestions for us, please submit them here. We particularly want to know about
          data issues, bugs or if you have ideas for new features.
          <AppNavigationContactForm type={INLINE_CONTACT_FORM_TYPES.FEEDBACK} />
        </>
      ),
    });
  };

  const { divider, nav, navButton, navTitle, base, buttonIcon, buttonText } = styles();

  return (
    <motion.div
      className={base()}
      initial="hidden"
      exit="hidden"
      animate={navIsOpen ? 'open' : 'hidden'}
      variants={{
        open: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeInOut' } },
        hidden: { opacity: 0, scale: 0.96 },
      }}
    >
      <div>
        <Heading variant="h5" className={navTitle()}>
          Help & Support
        </Heading>
        <ul className={nav()}>
          <li>
            <Link className={navButton()} href="/faqs" target="_blank">
              <span className={buttonIcon()}>
                <FileQuestion size={16} />
              </span>
              <span className={buttonText()}>
                Frequently asked questions
                <MoveUpRight size={14} />
              </span>
            </Link>
          </li>
          <li>
            <Link className={navButton()} href="/help" target="_blank">
              <span className={buttonIcon()}>
                <BookOpenText size={16} />
              </span>
              <span className={buttonText()}>
                How-to guides
                <MoveUpRight size={14} />
              </span>
            </Link>
          </li>
        </ul>
        <div className={divider()} />
        <ul className={nav()}>
          <li>
            <button type="button" className={navButton()} onClick={handleContactSupportClick}>
              <span className={buttonIcon()}>
                <Mail size={16} />
              </span>
              Contact support
            </button>
          </li>
          <li>
            <button type="button" className={navButton()} onClick={handleGiveFeedbackClick}>
              <span className={buttonIcon()}>
                <Send size={16} />
              </span>
              Send feedback
            </button>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

const styles = tv({
  slots: {
    base: 'grid min-w-64 max-w-xs origin-[var(--radix-popover-content-transform-origin)] gap-4 rounded-default bg-grey-100 p-4 pb-2 text-grey-800 shadow-md md:w-auto md:bg-white',
    divider: 'my-1 h-[1px] w-full bg-grey-200',
    nav: '-mx-2 text-baseSm',
    navButton:
      'flex w-full items-center rounded-md p-2 text-left text-baseSm leading-normal no-underline transition-colors duration-200 ease-in-out hover:bg-grey-100 disabled:opacity-50 [&_svg]:mr-1',
    navTitle: 'mb-1 pb-0.5 text-sm font-medium uppercase text-grey-500',
    buttonIcon: 'mr-1 grid place-items-center',
    buttonText: 'flex flex-1 items-center justify-between gap-2',
  },
});

export { AppNavigationHelpDropdown };
